import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { OvertimeReason } from "@/types/supabase-types";
import { Edit, Clock } from "lucide-react";

interface OvertimeReasonsTableProps {
  overtimeReasons: OvertimeReason[];
  onEdit: (reason: OvertimeReason) => void;
}

interface GroupedReasons {
  main_title: string;
  reasons: OvertimeReason[];
}

export function OvertimeReasonsTable({ overtimeReasons, onEdit }: OvertimeReasonsTableProps) {
  // Group reasons by main_title (L1 category)
  const groupedReasons: GroupedReasons[] = overtimeReasons.reduce((acc, reason) => {
    const existingGroup = acc.find(group => group.main_title === reason.main_title);
    if (existingGroup) {
      existingGroup.reasons.push(reason);
    } else {
      acc.push({ main_title: reason.main_title, reasons: [reason] });
    }
    return acc;
  }, [] as GroupedReasons[]);

  // Sort groups by main_title and reasons by reason text
  groupedReasons.sort((a, b) => a.main_title.localeCompare(b.main_title));
  groupedReasons.forEach(group => {
    group.reasons.sort((a, b) => (a.reason || '').localeCompare(b.reason || ''));
  });

  if (groupedReasons.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No overtime reasons configured yet.</p>
        <p className="text-sm text-muted-foreground mt-1">Add your first reason to get started.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40%] font-semibold">Category</TableHead>
            <TableHead className="w-[50%] font-semibold">Reasons</TableHead>
            <TableHead className="w-[10%] text-right font-semibold">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupedReasons.map((group) => (
            group.reasons.map((reason, reasonIndex) => (
              <TableRow key={reason.id} className={reasonIndex === 0 ? 'border-t-2 border-border' : ''}>
                <TableCell className="font-medium">
                  {reasonIndex === 0 && (
                    <Badge variant="secondary" className="mb-2">
                      {group.main_title}
                    </Badge>
                  )}
                  {reasonIndex === 0 && (
                    <div className="text-sm text-muted-foreground">
                      {group.reasons.length} reason{group.reasons.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm">{reason.reason || 'No specific reason'}</span>
                    {reason.code && (
                      <Badge variant="outline" className="text-xs">
                        {reason.code}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(reason)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="h-4 w-4" />
                    <span className="sr-only">Edit reason</span>
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
